import React, { useState, useEffect } from 'react';
import { 
  Car, 
  Search, 
  MapPin, 
  User, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  ChevronRight, 
  Calendar,
  X,
  Copy,
  Check,
  Navigation
} from 'lucide-react';
import { apiRequest } from '../utils/api';

export default function Rides() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL'); // ALL, ACTIVE, COMPLETED, CANCELLED
  const [copiedId, setCopiedId] = useState(null);

  // Detail Modal
  const [selectedRide, setSelectedRide] = useState(null);
  
  // Cancel confirmation dialog state
  const [cancelTargetRide, setCancelTargetRide] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const fetchRides = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await apiRequest('/api/admin/rides');
      if (!response.ok) {
        throw new Error('Erreur de chargement des courses');
      }
      const data = await response.json();
      setRides(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('Impossible de récupérer la liste des courses.');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchRides(true);
    // Poll every 15 seconds to keep dashboard up to date
    const interval = setInterval(() => fetchRides(false), 15000);
    return () => clearInterval(interval);
  }, []);

  const handleCancelRide = async (rideId) => {
    setCancelLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiRequest(`/api/admin/rides/${rideId}/cancel`, {
        method: 'POST'
      });
      
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Impossible d'annuler la course.");
      }
      
      setSuccess(result.message || 'La course a été annulée avec succès.');
      setCancelTargetRide(null);
      setSelectedRide(null);
      await fetchRides(false);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const isActiveStatus = (status) => {
    return [
      'SEARCHING_DRIVER',
      'DRIVER_PROPOSED',
      'DRIVER_ACCEPTED',
      'DRIVER_ARRIVING',
      'DRIVER_WAITING_PICKUP',
      'RIDE_STARTED'
    ].includes(status);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'SEARCHING_DRIVER': return 'Recherche chauffeur';
      case 'DRIVER_PROPOSED': return 'Chauffeur proposé';
      case 'DRIVER_ACCEPTED': return 'Chauffeur accepté';
      case 'DRIVER_ARRIVING': return 'Chauffeur en route';
      case 'DRIVER_WAITING_PICKUP': return 'Chauffeur au point de départ';
      case 'RIDE_STARTED': return 'Course en cours';
      case 'RIDE_COMPLETED': return 'Terminée';
      case 'CANCELLED_BY_CLIENT': return 'Annulée par le client';
      case 'CANCELLED_BY_DRIVER': return 'Annulée par le chauffeur';
      case 'CANCELLED_TIMEOUT': return 'Annulée (Timeout)';
      case 'CANCELLED_BY_ADMIN': return "Annulée par l'admin";
      case 'NO_DRIVER_FOUND': return 'Aucun chauffeur trouvé';
      case 'PAYMENT_FAILED': return 'Échec du paiement';
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    if (status === 'RIDE_COMPLETED') {
      return { text: '#00a34a', bg: 'rgba(0, 163, 74, 0.08)', border: 'rgba(0, 163, 74, 0.2)' };
    }
    if (status.startsWith('CANCELLED') || status === 'PAYMENT_FAILED') {
      return { text: '#ba1a1a', bg: 'rgba(186, 26, 26, 0.08)', border: 'rgba(186, 26, 26, 0.2)' };
    }
    if (isActiveStatus(status)) {
      return { text: '#0052ff', bg: 'rgba(0, 82, 255, 0.08)', border: 'rgba(0, 82, 255, 0.2)' };
    }
    return { text: '#b45309', bg: 'rgba(180, 83, 9, 0.08)', border: 'rgba(180, 83, 9, 0.2)' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats calculation
  const totalRides = rides.length;
  const activeRides = rides.filter(r => isActiveStatus(r.status)).length;
  const completedRides = rides.filter(r => r.status === 'RIDE_COMPLETED').length;
  const cancelledRides = rides.filter(r => r.status.startsWith('CANCELLED') || r.status === 'NO_DRIVER_FOUND').length;

  // Filter rides based on search query & status filter
  const filteredRides = rides.filter(ride => {
    const matchesSearch = ride.id.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filterStatus === 'ACTIVE') {
      return matchesSearch && isActiveStatus(ride.status);
    }
    if (filterStatus === 'COMPLETED') {
      return matchesSearch && ride.status === 'RIDE_COMPLETED';
    }
    if (filterStatus === 'CANCELLED') {
      return matchesSearch && (ride.status.startsWith('CANCELLED') || ride.status === 'NO_DRIVER_FOUND');
    }
    return matchesSearch;
  });

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div>
          <span style={styles.subtitle}>Supervisez les courses VTC en temps réel et gérez les annulations</span>
        </div>
      </div>

      {error && (
        <div style={styles.errorAlert}>
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button style={styles.closeAlertBtn} onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}

      {success && (
        <div style={styles.successAlert}>
          <CheckCircle size={18} />
          <span>{success}</span>
          <button style={styles.closeAlertBtn} onClick={() => setSuccess(null)}><X size={14} /></button>
        </div>
      )}

      {/* KPI Stats Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard} className="glass-card">
          <div style={styles.statIconContainer} className="bg-primary-glow">
            <Car size={20} color="#0052ff" />
          </div>
          <div>
            <span style={styles.statLabel}>Total Courses</span>
            <span style={styles.statValue}>{totalRides}</span>
          </div>
        </div>

        <div style={styles.statCard} className="glass-card">
          <div style={{ ...styles.statIconContainer, backgroundColor: 'rgba(0, 82, 255, 0.1)' }}>
            <Navigation size={20} color="#0052ff" />
          </div>
          <div>
            <span style={styles.statLabel}>En Cours</span>
            <span style={styles.statValue}>{activeRides}</span>
          </div>
        </div>

        <div style={styles.statCard} className="glass-card">
          <div style={{ ...styles.statIconContainer, backgroundColor: 'rgba(0, 163, 74, 0.1)' }}>
            <CheckCircle size={20} color="#00a34a" />
          </div>
          <div>
            <span style={styles.statLabel}>Terminées</span>
            <span style={styles.statValue}>{completedRides}</span>
          </div>
        </div>

        <div style={styles.statCard} className="glass-card">
          <div style={{ ...styles.statIconContainer, backgroundColor: 'rgba(186, 26, 26, 0.1)' }}>
            <XCircle size={20} color="#ba1a1a" />
          </div>
          <div>
            <span style={styles.statLabel}>Annulées / Échecs</span>
            <span style={styles.statValue}>{cancelledRides}</span>
          </div>
        </div>
      </div>

      {/* Toolbar / Search & Filter */}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input 
            type="text" 
            placeholder="Rechercher par ID de course (UUID)..." 
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={styles.clearSearchBtn}>
              <X size={14} color="#64748b" />
            </button>
          )}
        </div>

        <div style={styles.filterGroup}>
          <button 
            style={{ ...styles.filterTab, ...(filterStatus === 'ALL' ? styles.filterTabActive : {}) }}
            onClick={() => setFilterStatus('ALL')}
          >
            Toutes
          </button>
          <button 
            style={{ ...styles.filterTab, ...(filterStatus === 'ACTIVE' ? styles.filterTabActive : {}) }}
            onClick={() => setFilterStatus('ACTIVE')}
          >
            En Cours ({activeRides})
          </button>
          <button 
            style={{ ...styles.filterTab, ...(filterStatus === 'COMPLETED' ? styles.filterTabActive : {}) }}
            onClick={() => setFilterStatus('COMPLETED')}
          >
            Terminées ({completedRides})
          </button>
          <button 
            style={{ ...styles.filterTab, ...(filterStatus === 'CANCELLED' ? styles.filterTabActive : {}) }}
            onClick={() => setFilterStatus('CANCELLED')}
          >
            Annulées ({cancelledRides})
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div style={styles.tableCard} className="glass-card">
        {loading && rides.length === 0 ? (
          <div style={styles.loadingSpinner}>Chargement des courses...</div>
        ) : filteredRides.length === 0 ? (
          <div style={styles.emptyContainer}>
            <Car size={36} color="#94a3b8" style={{ marginBottom: '12px' }} />
            <span>Aucune course ne correspond à vos critères.</span>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID Course</th>
                  <th style={styles.th}>Date Demande</th>
                  <th style={styles.th}>Passager</th>
                  <th style={styles.th}>Chauffeur</th>
                  <th style={styles.th}>Départ / Arrivée</th>
                  <th style={styles.th}>Tarif</th>
                  <th style={styles.th}>Statut</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRides.map((ride) => {
                  const statusColors = getStatusColor(ride.status);
                  const isCancelable = isActiveStatus(ride.status);

                  return (
                    <tr key={ride.id} style={styles.tr}>
                      {/* ID with Copy */}
                      <td style={styles.td}>
                        <div style={styles.idContainer}>
                          <span style={styles.idText} title={ride.id}>{ride.id.substring(0, 8)}...</span>
                          <button 
                            onClick={() => copyToClipboard(ride.id)} 
                            style={styles.copyBtn}
                            title="Copier l'UUID complet"
                          >
                            {copiedId === ride.id ? <Check size={12} color="#00a34a" /> : <Copy size={12} color="#94a3b8" />}
                          </button>
                        </div>
                      </td>

                      {/* Date */}
                      <td style={styles.td}>
                        <div style={styles.dateCell}>
                          <Calendar size={14} color="#64748b" />
                          <span>{formatDate(ride.requestedAt)}</span>
                        </div>
                      </td>

                      {/* Passenger */}
                      <td style={styles.td}>
                        {ride.passenger ? (
                          <div style={styles.userCell}>
                            <span style={styles.userName}>{ride.passenger.firstName} {ride.passenger.lastName}</span>
                            <span style={styles.userEmail}>{ride.passenger.email}</span>
                          </div>
                        ) : (
                          <span style={styles.none}>N/A</span>
                        )}
                      </td>

                      {/* Driver */}
                      <td style={styles.td}>
                        {ride.driver ? (
                          <div style={styles.userCell}>
                            <span style={styles.userName}>{ride.driver.firstName} {ride.driver.lastName}</span>
                            <span style={styles.userEmail}>{ride.driver.email}</span>
                          </div>
                        ) : (
                          <span style={styles.unassigned}>Non assigné</span>
                        )}
                      </td>

                      {/* Addresses */}
                      <td style={{ ...styles.td, maxWidth: '220px' }}>
                        <div style={styles.addressCell}>
                          <div style={styles.addressItem}>
                            <MapPin size={12} color="#0052ff" style={{ flexShrink: 0 }} />
                            <span style={styles.addressText} title={ride.pickupAddress}>{ride.pickupAddress}</span>
                          </div>
                          <div style={styles.addressItem}>
                            <MapPin size={12} color="#ea580c" style={{ flexShrink: 0 }} />
                            <span style={styles.addressText} title={ride.destinationAddress}>{ride.destinationAddress}</span>
                          </div>
                        </div>
                      </td>

                      {/* Price */}
                      <td style={styles.td}>
                        <div style={styles.priceCell}>
                          <span style={styles.finalPrice}>
                            {ride.finalPrice ? `${ride.finalPrice.toFixed(2)} €` : `${ride.estimatedPrice?.toFixed(2) || '0.00'} €`}
                          </span>
                          {ride.discountAmount > 0 && (
                            <span style={styles.discountText}>- {ride.discountAmount.toFixed(2)} €</span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          color: statusColors.text,
                          backgroundColor: statusColors.bg,
                          borderColor: statusColors.border
                        }}>
                          {getStatusLabel(ride.status)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={styles.td}>
                        <div style={styles.actionCell}>
                          <button 
                            onClick={() => setSelectedRide(ride)} 
                            style={styles.detailBtn}
                            title="Détails"
                          >
                            <ChevronRight size={18} />
                          </button>
                          {isCancelable && (
                            <button 
                              onClick={() => setCancelTargetRide(ride)} 
                              style={styles.cancelBtn}
                              title="Annuler la course"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ride Details Modal */}
      {selectedRide && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent} className="glass-card">
            <div style={styles.modalHeader}>
              <div style={styles.modalTitleRow}>
                <Car size={20} color="#0052ff" />
                <h3 style={styles.modalTitle}>Détails de la Course</h3>
              </div>
              <button style={styles.modalCloseBtn} onClick={() => setSelectedRide(null)}>
                <X size={18} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalSection}>
                <h4 style={styles.sectionHeader}>Informations Générales</h4>
                <div style={styles.infoGrid}>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>ID de Course (UUID)</span>
                    <div style={styles.idContainer}>
                      <span style={styles.infoValue}>{selectedRide.id}</span>
                      <button onClick={() => copyToClipboard(selectedRide.id)} style={styles.copyBtn}>
                        {copiedId === selectedRide.id ? <Check size={12} color="#00a34a" /> : <Copy size={12} />}
                      </button>
                    </div>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Statut</span>
                    <span style={{
                      ...styles.statusBadge,
                      width: 'fit-content',
                      color: getStatusColor(selectedRide.status).text,
                      backgroundColor: getStatusColor(selectedRide.status).bg,
                      borderColor: getStatusColor(selectedRide.status).border
                    }}>
                      {getStatusLabel(selectedRide.status)}
                    </span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Date de demande</span>
                    <span style={styles.infoValue}>{formatDate(selectedRide.requestedAt)}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Début de course</span>
                    <span style={styles.infoValue}>{formatDate(selectedRide.startedAt)}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Fin de course</span>
                    <span style={styles.infoValue}>{formatDate(selectedRide.completedAt)}</span>
                  </div>
                  <div style={styles.infoItem}>
                    <span style={styles.infoLabel}>Tarif Estimé / Final</span>
                    <span style={styles.infoValue}>
                      {selectedRide.estimatedPrice?.toFixed(2)} € / {selectedRide.finalPrice ? `${selectedRide.finalPrice.toFixed(2)} €` : 'En attente'}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.modalSection}>
                <h4 style={styles.sectionHeader}>Itinéraire</h4>
                <div style={styles.addressDetails}>
                  <div style={styles.routeItem}>
                    <div style={styles.routeDotGreen} />
                    <div style={styles.routeTextContainer}>
                      <span style={styles.routeLabel}>Départ</span>
                      <span style={styles.routeVal}>{selectedRide.pickupAddress}</span>
                      <span style={styles.coords}>GPS: {selectedRide.pickupLatitude?.toFixed(6)}, {selectedRide.pickupLongitude?.toFixed(6)}</span>
                    </div>
                  </div>
                  <div style={styles.routeLine} />
                  <div style={styles.routeItem}>
                    <div style={styles.routeDotRed} />
                    <div style={styles.routeTextContainer}>
                      <span style={styles.routeLabel}>Destination</span>
                      <span style={styles.routeVal}>{selectedRide.destinationAddress}</span>
                      <span style={styles.coords}>GPS: {selectedRide.destinationLatitude?.toFixed(6)}, {selectedRide.destinationLongitude?.toFixed(6)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div style={styles.dualGrid}>
                {/* Passenger details */}
                <div style={styles.panelBlock}>
                  <h4 style={styles.sectionHeader}>Passager</h4>
                  {selectedRide.passenger ? (
                    <div style={styles.personCard}>
                      <div style={styles.personHeader}>
                        <div style={styles.avatarPlaceholder} className="bg-primary-glow">
                          <User size={18} color="#0052ff" />
                        </div>
                        <div>
                          <span style={styles.personName}>{selectedRide.passenger.firstName} {selectedRide.passenger.lastName}</span>
                          <span style={styles.personRole}>Passager Safe</span>
                        </div>
                      </div>
                      <div style={styles.personDetailGrid}>
                        <div style={styles.personDetailRow}>
                          <span style={styles.pdLabel}>ID</span>
                          <span style={styles.pdVal}>{selectedRide.passenger.id.substring(0, 8)}...</span>
                        </div>
                        <div style={styles.personDetailRow}>
                          <span style={styles.pdLabel}>Email</span>
                          <span style={styles.pdVal}>{selectedRide.passenger.email}</span>
                        </div>
                        <div style={styles.personDetailRow}>
                          <span style={styles.pdLabel}>Téléphone</span>
                          <span style={styles.pdVal}>{selectedRide.passenger.phone || 'Non renseigné'}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.emptyPerson}>Aucun passager associé.</div>
                  )}
                </div>

                {/* Driver details */}
                <div style={styles.panelBlock}>
                  <h4 style={styles.sectionHeader}>Chauffeur</h4>
                  {selectedRide.driver ? (
                    <div style={styles.personCard}>
                      <div style={styles.personHeader}>
                        <div style={{ ...styles.avatarPlaceholder, backgroundColor: 'rgba(0, 163, 74, 0.1)' }}>
                          <User size={18} color="#00a34a" />
                        </div>
                        <div>
                          <span style={styles.personName}>{selectedRide.driver.firstName} {selectedRide.driver.lastName}</span>
                          <span style={styles.personRole}>Chauffeur Validé</span>
                        </div>
                      </div>
                      <div style={styles.personDetailGrid}>
                        <div style={styles.personDetailRow}>
                          <span style={styles.pdLabel}>Véhicule</span>
                          <span style={styles.pdVal}>{selectedRide.driver.vehicleBrand} {selectedRide.driver.vehicleModel}</span>
                        </div>
                        <div style={styles.personDetailRow}>
                          <span style={styles.pdLabel}>Immatriculation</span>
                          <span style={styles.pdVal}>{selectedRide.driver.licensePlate || 'N/A'}</span>
                        </div>
                        <div style={styles.personDetailRow}>
                          <span style={styles.pdLabel}>Email</span>
                          <span style={styles.pdVal}>{selectedRide.driver.email}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={styles.emptyPerson}>Chauffeur non encore assigné.</div>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button 
                style={styles.modalCancelBtn} 
                onClick={() => setSelectedRide(null)}
              >
                Fermer
              </button>
              {isActiveStatus(selectedRide.status) && (
                <button 
                  style={styles.modalActionBtn}
                  onClick={() => setCancelTargetRide(selectedRide)}
                >
                  <XCircle size={16} />
                  <span>Annuler la Course</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Confirmation Dialog */}
      {cancelTargetRide && (
        <div style={styles.modalOverlay} className="confirm-overlay">
          <div style={styles.confirmBox} className="glass-card">
            <div style={styles.confirmHeader}>
              <AlertTriangle size={24} color="#ba1a1a" />
              <h3 style={styles.confirmTitle}>Annuler la course ?</h3>
            </div>
            
            <p style={styles.confirmText}>
              Êtes-vous sûr de vouloir forcer l'annulation de la course <strong>{cancelTargetRide.id.substring(0, 8)}...</strong> ? 
              Cette action remboursera le solde du passager et libérera le chauffeur assigné.
            </p>
            
            <div style={styles.confirmFooter}>
              <button 
                style={styles.confirmCancelBtn} 
                onClick={() => setCancelTargetRide(null)}
                disabled={cancelLoading}
              >
                Retour
              </button>
              <button 
                style={styles.confirmSubmitBtn}
                onClick={() => handleCancelRide(cancelTargetRide.id)}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Annulation...' : 'Confirmer l\'annulation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1280px',
    margin: '0 auto',
    animation: 'fadeIn 0.3s ease-out',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    height: '100%',
    overflowY: 'auto'
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '8px'
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--text-secondary)'
  },
  errorAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'var(--danger-bg)',
    border: '0.5px solid var(--danger-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--danger)',
    fontSize: '14px',
    position: 'relative'
  },
  successAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    backgroundColor: 'var(--success-bg)',
    border: '0.5px solid var(--success-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--success)',
    fontSize: '14px',
    position: 'relative'
  },
  closeAlertBtn: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px'
  },
  statCard: {
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    borderRadius: 'var(--radius-main)',
    background: 'var(--bg-secondary)',
    border: '0.5px solid var(--border-color)',
    boxShadow: 'var(--shadow-main)'
  },
  statIconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statLabel: {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-secondary)',
    fontWeight: '500',
    marginBottom: '4px'
  },
  statValue: {
    display: 'block',
    fontSize: '24px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flex: '1',
    minWidth: '280px',
    maxWidth: '480px'
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    pointerEvents: 'none'
  },
  searchInput: {
    width: '100%',
    padding: '12px 16px 12px 42px',
    fontSize: '14px',
    borderRadius: 'var(--radius-main)',
    border: '0.5px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    outline: 'none',
    boxShadow: 'var(--shadow-main)',
    transition: 'border-color 0.2s'
  },
  clearSearchBtn: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  filterGroup: {
    display: 'flex',
    gap: '6px',
    backgroundColor: 'var(--bg-tertiary)',
    padding: '4px',
    borderRadius: '10px',
    border: '0.5px solid var(--border-color)'
  },
  filterTab: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    borderRadius: '8px',
    border: 'none',
    background: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  filterTabActive: {
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--primary)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    fontWeight: '600'
  },
  tableCard: {
    borderRadius: 'var(--radius-main)',
    background: 'var(--bg-secondary)',
    border: '0.5px solid var(--border-color)',
    boxShadow: 'var(--shadow-main)',
    overflow: 'hidden'
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  th: {
    padding: '16px 20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '0.5px solid var(--border-color)',
    backgroundColor: '#fafafa'
  },
  tr: {
    borderBottom: '0.5px solid var(--border-color)',
    transition: 'background-color 0.2s'
  },
  td: {
    padding: '16px 20px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    verticalAlign: 'middle'
  },
  idContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  idText: {
    fontFamily: 'monospace',
    fontSize: '13px',
    color: 'var(--text-secondary)'
  },
  copyBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    transition: 'background-color 0.2s'
  },
  dateCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: 'var(--text-secondary)'
  },
  userCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  userName: {
    fontWeight: '500',
    color: 'var(--text-primary)'
  },
  userEmail: {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  },
  none: {
    color: 'var(--text-muted)',
    fontSize: '13px'
  },
  unassigned: {
    color: 'var(--warning)',
    backgroundColor: 'var(--warning-bg)',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '500',
    border: '0.5px solid var(--warning-border)',
    width: 'fit-content'
  },
  addressCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  addressItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  addressText: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  priceCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    alignItems: 'flex-start'
  },
  finalPrice: {
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  discountText: {
    fontSize: '11px',
    color: 'var(--danger)',
    fontWeight: '500'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '30px',
    fontSize: '12px',
    fontWeight: '500',
    border: '0.5px solid',
    textAlign: 'center'
  },
  actionCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  detailBtn: {
    border: '0.5px solid var(--border-color)',
    background: 'var(--bg-secondary)',
    cursor: 'pointer',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    transition: 'all 0.2s'
  },
  cancelBtn: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--danger)',
    transition: 'transform 0.2s'
  },
  loadingSpinner: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '14px'
  },
  emptyContainer: {
    padding: '60px 40px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    fontSize: '14px'
  },
  modalOverlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease-out'
  },
  modalContent: {
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-main)',
    border: '0.5px solid var(--border-color)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '0.5px solid var(--border-color)'
  },
  modalTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    padding: '4px'
  },
  modalBody: {
    padding: '24px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  modalSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  sectionHeader: {
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '6px'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px'
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  infoLabel: {
    fontSize: '12px',
    color: 'var(--text-secondary)'
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)'
  },
  addressDetails: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    gap: '12px',
    padding: '8px 0'
  },
  routeItem: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start'
  },
  routeDotGreen: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#00a34a',
    marginTop: '6px',
    flexShrink: 0
  },
  routeDotRed: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: '#ba1a1a',
    marginTop: '6px',
    flexShrink: 0
  },
  routeLine: {
    position: 'absolute',
    left: '5px',
    top: '18px',
    bottom: '18px',
    width: '2px',
    backgroundColor: 'var(--border-color)',
    zIndex: 1
  },
  routeTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  routeLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase'
  },
  routeVal: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-primary)'
  },
  coords: {
    fontSize: '11px',
    color: 'var(--text-muted)'
  },
  dualGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    flexWrap: 'wrap'
  },
  panelBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  personCard: {
    padding: '16px',
    borderRadius: 'var(--radius-sm)',
    border: '0.5px solid var(--border-color)',
    backgroundColor: '#fafafa',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  personHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  avatarPlaceholder: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  personName: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  personRole: {
    display: 'block',
    fontSize: '11px',
    color: 'var(--text-secondary)'
  },
  personDetailGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  personDetailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    borderBottom: '0.5px dashed var(--border-color)',
    paddingBottom: '4px'
  },
  pdLabel: {
    color: 'var(--text-secondary)'
  },
  pdVal: {
    fontWeight: '500',
    color: 'var(--text-primary)'
  },
  emptyPerson: {
    padding: '24px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    border: '0.5px dashed var(--border-color)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '13px'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: '0.5px solid var(--border-color)',
    gap: '12px'
  },
  modalCancelBtn: {
    padding: '10px 18px',
    borderRadius: 'var(--radius-sm)',
    border: '0.5px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--text-secondary)',
    cursor: 'pointer'
  },
  modalActionBtn: {
    padding: '10px 18px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    backgroundColor: 'var(--danger)',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  confirmBox: {
    width: '90%',
    maxWidth: '440px',
    padding: '24px',
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-main)',
    border: '0.5px solid var(--border-color)',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    animation: 'fadeIn 0.15s ease-out'
  },
  confirmHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  confirmTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)'
  },
  confirmText: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    lineHeight: '1.5'
  },
  confirmFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '8px'
  },
  confirmCancelBtn: {
    padding: '8px 16px',
    borderRadius: 'var(--radius-sm)',
    border: '0.5px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    fontSize: '13px',
    color: 'var(--text-secondary)',
    cursor: 'pointer'
  },
  confirmSubmitBtn: {
    padding: '8px 16px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    backgroundColor: 'var(--danger)',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};
